import { forwardRef, type JSX, type ReactNode, type RefObject } from 'react';

import { CategoricalBarChartInternal } from './CategoricalBarChartInternal.js';
import { CategoricalBarChartLegend } from './components/legend/CategoricalBarChartLegend.js';
import { useCategoricalBarChartBaseConfig } from './hooks/useCategoricalBarChartBaseConfig.js';
import { useCategoricalBarChartData } from './hooks/useCategoricalBarChartData.js';
import { CategoricalBarChartLegendConfiguration } from './types/categorical-bar-chart.js';
import { isGrouped, isStacked } from './utils/layout.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { ChartLayout } from '../core/components/chart-layout/ChartLayout.js';
import type { ChartLayoutRef } from '../core/components/chart-layout/types/chart-layout.js';

interface CategoricalBarChartLayoutProps extends StylingProps, MaskingProps {
  emptyState?: ReactNode;
  errorState: ReactNode | ((errorMessage: string) => JSX.Element) | undefined;
  legend: CategoricalBarChartLegendConfiguration;
  isEmpty: boolean;
  loading: boolean;
  height?: string;
  width?: number | string;
  chartLayoutRef: RefObject<ChartLayoutRef | null>;
  /**
   * Component identifier for behavioral tracking
   * @internal
   */
  'data-dt-component'?: string;
}

export const CategoricalBarChartLayout = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  CategoricalBarChartLayoutProps
>((props, forwardedRef) => {
  const {
    height,
    width,
    legend,
    isEmpty,
    loading,
    errorState,
    emptyState,
    className,
    style,
    chartLayoutRef,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'data-dt-component': dataDtComponent,
  } = props;
  const [behaviorTrackingProps] = useBehavioralTrackingProps(props);
  const { uniqueDimensions } = useCategoricalBarChartData();
  const { groupMode } = useCategoricalBarChartBaseConfig();

  const canShowLegend =
    isGrouped(groupMode) ||
    (isStacked(groupMode) && uniqueDimensions.length > 1);
  const {
    position: legendPosition,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    ratio,
    initialRatio,
    onRatioChange,
  } = legend;

  const legendRatio = initialRatio ?? ratio;

  const showLegend = canShowLegend && !legend.hidden;

  return (
    <ChartLayout
      ref={chartLayoutRef}
      chartHeight={height}
      chartWidth={width}
      isEmpty={isEmpty}
      emptyState={emptyState}
      errorState={errorState}
      showLoader={loading}
      className={className}
      style={style}
      {...behaviorTrackingProps}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      data-dt-component={dataDtComponent}
    >
      <ChartLayout.Graph>
        <CategoricalBarChartInternal ref={forwardedRef} />
      </ChartLayout.Graph>
      {showLegend && (
        <ChartLayout.Legend
          position={legendPosition}
          initialRatio={legendRatio}
          onResize={onRatioChange}
        >
          <CategoricalBarChartLegend />
        </ChartLayout.Legend>
      )}
    </ChartLayout>
  );
});

(
  CategoricalBarChartLayout as typeof CategoricalBarChartLayout & {
    displayName: string;
  }
).displayName = 'CategoricalBarChartLayout';
